import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Phone, Mail, MapPin } from 'lucide-react';
import { getMenuItems, resolveCanonicalSlug } from '../lib/cms';

export default function Header() {
  const [menuItems, setMenuItems] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    async function loadMenu() {
      const items = await getMenuItems();
      setMenuItems(items);
    }
    loadMenu();
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setDropdownOpen(false);
    setMobileDropdownOpen(false);
  }, [location]);

  // Separate parent items and child items
  const parentItemsFromCms = menuItems.filter(item => !item.parent_label);
  const getChildren = (parentLabel) => menuItems.filter(item => item.parent_label === parentLabel);

  const hasForside = parentItemsFromCms.some(item => item.label === 'Forside');
  const parentItems = hasForside
    ? parentItemsFromCms
    : [{ id: 'forside', label: 'Forside', href: '/' }, ...parentItemsFromCms];

  const isActive = (href) => {
    if (href === '#' || href === '') return false;
    if (href === '/' && location.pathname === '/') return true;
    if (location.pathname === href) return true;

    const currentCanonical = resolveCanonicalSlug(location.pathname);
    const targetCanonical = resolveCanonicalSlug(href);
    return currentCanonical === targetCanonical && currentCanonical !== 'index';
  };

  const isParentActive = (parentLabel) => {
    const children = getChildren(parentLabel);
    return children.some(child => isActive(child.href));
  };

  // Split parent items for the 3-part desktop layout
  const leftLabels = ['Forside', 'Vi tilbyder', 'Profil'];
  const rightLabels = ['Vores lastbiler', 'Kontakt'];

  const leftItems = parentItems.filter(item => leftLabels.includes(item.label));
  const rightItems = parentItems.filter(item => rightLabels.includes(item.label));

  const renderNavItem = (item, isMobile = false) => {
    const children = getChildren(item.label);
    const hasChildren = children.length > 0;

    if (hasChildren) {
      if (isMobile) {
        return (
          <div key={item.id || item.label} className="flex flex-col">
            <button
              onClick={() => setMobileDropdownOpen(!mobileDropdownOpen)}
              className={`flex justify-between items-center w-full px-4 py-3 rounded-none font-bold text-left transition-colors uppercase tracking-wider text-xs ${
                isParentActive(item.label)
                  ? 'text-brand-red bg-neutral-surface-light'
                  : 'text-text-primary hover:bg-neutral-surface-light'
              }`}
            >
              {item.label}
              <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${mobileDropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            {mobileDropdownOpen && (
              <div className="pl-4 bg-neutral-surface-light/50 flex flex-col">
                {children.map((child) => (
                  <Link
                    key={child.id || child.label}
                    to={child.href}
                    className={`block px-4 py-2.5 text-xs font-semibold uppercase tracking-wider transition-colors ${
                      isActive(child.href)
                        ? 'text-brand-red font-bold'
                        : 'text-text-secondary hover:text-brand-red'
                    }`}
                  >
                    {child.label}
                  </Link>
                ))}
              </div>
            )}
          </div>
        );
      }

      return (
        <div
          key={item.id || item.label}
          className="relative"
          onMouseEnter={() => setDropdownOpen(true)}
          onMouseLeave={() => setDropdownOpen(false)}
        >
          <button
            className={`flex items-center gap-1 px-4 py-2 font-bold text-xs uppercase tracking-wider transition-colors cursor-pointer ${
              isParentActive(item.label)
                ? 'text-brand-red'
                : 'text-text-primary hover:text-brand-red'
            }`}
          >
            {item.label}
            <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`} />
          </button>

          {/* Dropdown Menu */}
          {dropdownOpen && (
            <div className="absolute left-0 mt-1 w-64 bg-white border border-border-light rounded-none shadow-lg py-2 z-50 animate-in fade-in slide-in-from-top-2 duration-150">
              {children.map((child) => (
                <Link
                  key={child.id || child.label}
                  to={child.href}
                  className={`block px-4 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors ${
                    isActive(child.href)
                      ? 'text-brand-red bg-neutral-surface-light'
                      : 'text-text-secondary hover:text-brand-red hover:bg-neutral-surface-light'
                  }`}
                >
                  {child.label}
                </Link>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (isMobile) {
      return (
        <Link
          key={item.id || item.label}
          to={item.href}
          className={`px-4 py-3 rounded-none font-bold uppercase tracking-wider text-xs transition-colors ${
            isActive(item.href)
              ? 'text-brand-red bg-neutral-surface-light'
              : 'text-text-primary hover:bg-neutral-surface-light'
          }`}
        >
          {item.label}
        </Link>
      );
    }

    return (
      <Link
        key={item.id || item.label}
        to={item.href}
        className={`px-4 py-2 font-bold text-xs uppercase tracking-wider transition-colors ${
          isActive(item.href)
            ? 'text-brand-red'
            : 'text-text-primary hover:text-brand-red'
        }`}
      >
        {item.label}
      </Link>
    );
  };

  return (
    <header className="relative z-50 w-full bg-white">
      {/* Top Utility Bar */}
      <div className="h-9 bg-white border-b border-border-light text-xs text-text-muted">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center justify-between sm:justify-end gap-6">
          <span className="hidden sm:flex items-center gap-1.5 whitespace-nowrap">
            <MapPin className="h-3.5 w-3.5 text-text-muted shrink-0" />
            Industriparken 5, 8832 Skals
          </span>
          <div className="flex items-center gap-6 w-full sm:w-auto justify-between sm:justify-end">
            <a href="tel:40458650" className="flex items-center gap-1.5 hover:text-brand-red transition-colors whitespace-nowrap font-medium">
              <Phone className="h-3.5 w-3.5 text-text-muted shrink-0" />
              Tlf: <span className="whitespace-nowrap">40 45 86 50</span>
            </a>
            <a href="mailto:evskals@gmail.com" className="flex items-center gap-1.5 hover:text-brand-red transition-colors whitespace-nowrap font-medium">
              <Mail className="h-3.5 w-3.5 text-text-muted shrink-0" />
              <span className="whitespace-nowrap">evskals@gmail.com</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main Header Bar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex justify-between items-center">
        {/* Desktop Left Nav (Visible at >= 1200px) */}
        <nav className="hidden min-[1200px]:flex items-center gap-1 w-1/3 justify-start">
          {leftItems.map(item => renderNavItem(item, false))}
        </nav>

        {/* Logo (Centered on desktop, Left on mobile) */}
        <div className="flex justify-start min-[1200px]:justify-center min-[1200px]:w-1/3 h-full items-center">
          <Link to="/" className="flex items-center h-full py-2">
            <img
              src="https://assets.auxodev.dk/assets/3c12e69c-355b-4bf8-827c-6379a757f0e7/skals-vognmandsforretning-logo.png"
              alt="Skals Vognmandsforretning - Vognmandskørsel i Skals, Viborg og Skive"
              className="h-14 sm:h-16 w-auto object-contain"
            />
          </Link>
        </div>

        {/* Desktop Right Nav (Visible at >= 1200px) */}
        <nav className="hidden min-[1200px]:flex items-center gap-1 w-1/3 justify-end">
          {rightItems.map(item => renderNavItem(item, false))}
        </nav>

        {/* Burger Menu Button (Visible at < 1200px) */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="flex min-[1200px]:hidden p-2 rounded-none text-text-primary hover:bg-neutral-surface-light transition-colors focus:outline-none"
          aria-label="Toggle menu"
        >
          {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile Navigation Drawer (Visible at < 1200px) */}
      {mobileMenuOpen && (
        <div className="min-[1200px]:hidden absolute top-full left-0 w-full bg-white border-t border-border-light shadow-xl py-4 px-4 z-40 animate-in slide-in-from-top duration-200">
          <div className="flex flex-col gap-1">
            {parentItems.map(item => renderNavItem(item, true))}
          </div>
        </div>
      )}
    </header>
  );
}
